/**
 * IndividualDashboard - Dashboard view showing individual developer metrics
 *
 * Displays:
 * - Quick insight cards with key metrics filtered by author
 * - Data table with detailed breakdown for selected developer
 * - Author selector in page header
 * - Stacked bar charts showing commit distribution by classification and size
 */

import {
  Badge,
  DataTable,
  GroupedBarChart,
  InsightDashboard,
  InsightDashboardStore,
  MetricsCard,
  PageHeader,
  type SelectOption,
  StackedBarChart,
  type TabDefinition,
  TabsWithStore,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useMemo, useState } from "react"
import { updateUrlParams } from "../hooks/useUrlParams"
import { getClassificationBadgeColor, getScoreBadgeColor } from "../lib/badgeColors"
import { getFromStorage, StorageKeys, saveToStorage } from "../lib/localStorage"
import { logger } from "../lib/logger"
import {
  calculateAverageDailyPerformance,
  calculateMetrics,
  calculatePerformanceScore,
} from "../lib/performanceScore"
import { routes } from "../routes/routes"
import { useDataStore, useI18nStore, useSettingsStore, useStore } from "../stores/StoreContext"
import type { ComplexityClass } from "../types/data"

type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

interface IndividualDashboardProps {
  timeRange?: TimeRange
  /** When true, the component will sync the selected contributor to the URL. Default: false */
  syncToUrl?: boolean
  /** Currently selected tab ID (controlled mode) */
  selectedTab?: string
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void
  /** Initial contributor to select (from URL query param) */
  initialContributor?: string
}

// Helper to get the time range translation key
const getTimeRangeKey = (
  timeRange: TimeRange,
): "timeLast7Days" | "timeLast14Days" | "timeLast28Days" | "timeLast8Weeks" | "timeLast12Weeks" => {
  switch (timeRange) {
    case "7days":
      return "timeLast7Days"
    case "14days":
      return "timeLast14Days"
    case "28days":
      return "timeLast28Days"
    case "8weeks":
      return "timeLast8Weeks"
    case "12weeks":
      return "timeLast12Weeks"
    default:
      return "timeLast7Days"
  }
}

// Helper to get the comparison translation key
const getComparisonKey = (
  timeRange: TimeRange,
):
  | "quickInsightVsLast7Days"
  | "quickInsightVsLast14Days"
  | "quickInsightVsLast28Days"
  | "quickInsightVsLast8Weeks"
  | "quickInsightVsLast12Weeks" => {
  switch (timeRange) {
    case "7days":
      return "quickInsightVsLast7Days"
    case "14days":
      return "quickInsightVsLast14Days"
    case "28days":
      return "quickInsightVsLast28Days"
    case "8weeks":
      return "quickInsightVsLast8Weeks"
    case "12weeks":
      return "quickInsightVsLast12Weeks"
    default:
      return "quickInsightVsLast7Days"
  }
}

// Helper to get date range start based on time range
const getDateRangeStart = (timeRange: TimeRange): Date => {
  const now = new Date()
  if (timeRange === "7days") {
    return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "14days") {
    return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "28days") {
    return new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "8weeks") {
    return new Date(now.getTime() - 8 * 7 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "12weeks") {
    return new Date(now.getTime() - 12 * 7 * 24 * 60 * 60 * 1000)
  }
  return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000) // Default to 7 days
}

// Helper to get comparison period start based on time range
const getComparisonPeriodStart = (timeRange: TimeRange): Date => {
  const now = new Date()
  if (timeRange === "7days") {
    return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "14days") {
    return new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "28days") {
    return new Date(now.getTime() - 56 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "8weeks") {
    return new Date(now.getTime() - 16 * 7 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "12weeks") {
    return new Date(now.getTime() - 24 * 7 * 24 * 60 * 60 * 1000)
  }
  return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000) // Default to 14 days ago
}

export const IndividualDashboard = observer(
  ({
    timeRange = "7days",
    syncToUrl = false,
    selectedTab,
    onTabChange,
    initialContributor,
  }: IndividualDashboardProps) => {
    const store = useStore()
    const dataStore = useDataStore()
    const i18nStore = useI18nStore()
    const settingsStore = useSettingsStore()

    // Get the time range label for insight cards
    const timeRangeLabel = i18nStore.t(getTimeRangeKey(timeRange))
    // Get the comparison translation key
    const comparisonKey = getComparisonKey(timeRange)

    // Calculate date ranges based on selected time range
    const dateRange = useMemo(() => getDateRangeStart(timeRange), [timeRange])
    const comparisonDateRange = useMemo(() => getComparisonPeriodStart(timeRange), [timeRange])

    // Get all unique authors sorted by handle
    const authors = useMemo(() => {
      return dataStore.allAuthors.sort((a, b) => a.handle.localeCompare(b.handle))
    }, [dataStore.allAuthors])

    // Get author from URL route params if available
    const routeParams = store.router.params as { handle?: string } | undefined
    const authorFromUrl = routeParams?.handle ? decodeURIComponent(routeParams.handle) : undefined

    // Get contributor from URL query params
    const queryParams = new URLSearchParams(window.location.search)
    const contributorFromQuery = queryParams.get("contributor") || undefined

    // Initialize author selection from localStorage, URL, or default to first author
    const [selectedAuthorHandle, setSelectedAuthorHandle] = useState<string>(() => {
      const handles = authors.map((a) => a.handle)
      // Priority: initialContributor > URL query param > URL route param > localStorage > first author
      if (initialContributor && handles.includes(initialContributor)) {
        return initialContributor
      }
      if (contributorFromQuery && handles.includes(contributorFromQuery)) {
        return contributorFromQuery
      }
      if (authorFromUrl && handles.includes(authorFromUrl)) {
        return authorFromUrl
      }
      const saved = getFromStorage(StorageKeys.SELECTED_CONTRIBUTOR)
      if (saved && handles.includes(saved)) {
        return saved
      }
      return authors.length > 0 && authors[0] ? authors[0].handle : ""
    })

    // Initialize author selection based on URL or localStorage or default to first author
    useEffect(() => {
      const handles = authors.map((a) => a.handle)
      if (authors.length > 0 && !selectedAuthorHandle) {
        if (initialContributor && handles.includes(initialContributor)) {
          setSelectedAuthorHandle(initialContributor)
        } else if (contributorFromQuery && handles.includes(contributorFromQuery)) {
          setSelectedAuthorHandle(contributorFromQuery)
        } else if (authorFromUrl && handles.includes(authorFromUrl)) {
          setSelectedAuthorHandle(authorFromUrl)
        } else {
          const saved = getFromStorage(StorageKeys.SELECTED_CONTRIBUTOR)
          if (saved && handles.includes(saved)) {
            setSelectedAuthorHandle(saved)
          } else if (authors[0]) {
            setSelectedAuthorHandle(authors[0].handle)
          }
        }
      }
    }, [authors, authorFromUrl, contributorFromQuery, selectedAuthorHandle, initialContributor])

    // Sync URL when author changes (only if syncToUrl is enabled)
    useEffect(() => {
      const handles = authors.map((a) => a.handle)
      if (
        syncToUrl &&
        selectedAuthorHandle &&
        selectedAuthorHandle !== authorFromUrl &&
        handles.includes(selectedAuthorHandle)
      ) {
        // Update URL to include the selected contributor
        store.router.goTo(routes.contributorDetail, {
          handle: encodeURIComponent(selectedAuthorHandle),
        } as Record<string, string>)
      }
    }, [syncToUrl, selectedAuthorHandle, authorFromUrl, store.router, authors])

    // Update selected author when URL changes externally
    useEffect(() => {
      const handles = authors.map((a) => a.handle)
      if (
        authorFromUrl &&
        authorFromUrl !== selectedAuthorHandle &&
        handles.includes(authorFromUrl)
      ) {
        setSelectedAuthorHandle(authorFromUrl)
      }
    }, [authorFromUrl, authors, selectedAuthorHandle])

    // Update selected author when query param changes
    useEffect(() => {
      const handles = authors.map((a) => a.handle)
      if (
        contributorFromQuery &&
        contributorFromQuery !== selectedAuthorHandle &&
        handles.includes(contributorFromQuery)
      ) {
        setSelectedAuthorHandle(contributorFromQuery)
      }
    }, [contributorFromQuery, authors, selectedAuthorHandle])

    // Update selected author when initialContributor prop changes
    useEffect(() => {
      const handles = authors.map((a) => a.handle)
      if (
        initialContributor &&
        initialContributor !== selectedAuthorHandle &&
        handles.includes(initialContributor)
      ) {
        setSelectedAuthorHandle(initialContributor)
      }
    }, [initialContributor, authors, selectedAuthorHandle])

    // Handle author selection change
    const handleAuthorChange = useCallback((authorHandle: string) => {
      setSelectedAuthorHandle(authorHandle)
      saveToStorage(StorageKeys.SELECTED_CONTRIBUTOR, authorHandle)
      // Update URL query params to reflect the new contributor selection
      updateUrlParams({ contributor: authorHandle })
    }, [])

    // Create select options for authors
    const authorOptions: SelectOption[] = useMemo(
      () =>
        authors.map((author) => ({
          id: author.handle,
          label: author.handle,
        })),
      [authors],
    )

    // Calculate performance score for the selected individual
    const performanceScoreData = useMemo(() => {
      const commits = dataStore.allCommits.filter(
        (c) => c.authorHandle === selectedAuthorHandle && c.timestampDate >= dateRange,
      )
      const prs = dataStore.allPullRequests.filter(
        (pr) => pr.creatorHandle === selectedAuthorHandle && pr.createdAtDate >= dateRange,
      )
      const issues = dataStore.allIssues.filter(
        (issue) =>
          issue.closedAtDate &&
          issue.closedAtDate >= dateRange &&
          (issue.closedByHandle === selectedAuthorHandle ||
            issue.creatorHandle === selectedAuthorHandle),
      )

      // Calculate time range in days (from dateRange to yesterday)
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      yesterday.setHours(23, 59, 59, 999)
      const days = Math.ceil((yesterday.getTime() - dateRange.getTime()) / (24 * 60 * 60 * 1000))

      // Calculate average daily performance score (excluding weekends with no activity)
      // Note: calculateAverageDailyPerformance now defaults to yesterday as end date
      const avgDailyScore = calculateAverageDailyPerformance(commits, prs, issues, dateRange)

      // Create a performance score object with the calculated average
      const score = {
        overall: avgDailyScore,
        breakdown: {
          quality: 0,
          throughput: 0,
          impact: 0,
          problemSolving: 0,
          versatility: 0,
        },
        metrics: {
          commitScore: 0,
          commitCount: 0,
          linesAdded: 0,
          linesRemoved: 0,
          filesChanged: 0,
          prCount: 0,
          prAdditions: 0,
          prDeletions: 0,
          issuesResolved: 0,
          featureCommits: 0,
          bugfixCommits: 0,
          refactoringCommits: 0,
          otherCommits: 0,
        },
      }

      // Generate daily performance values (up to yesterday)
      const dailyValues = []
      for (let i = days - 1; i >= 0; i--) {
        const dayStart = new Date(yesterday.getTime() - i * 24 * 60 * 60 * 1000)
        dayStart.setHours(0, 0, 0, 0)
        const dayEnd = new Date(dayStart)
        dayEnd.setHours(23, 59, 59, 999)

        const dayCommits = commits.filter(
          (c) => c.timestampDate >= dayStart && c.timestampDate <= dayEnd,
        )
        const dayPRs = prs.filter(
          (pr) =>
            pr.createdAtDate &&
            pr.createdAtDate >= dayStart &&
            pr.createdAtDate <= dayEnd &&
            pr.state === "merged",
        )
        const dayIssues = issues.filter(
          (issue) =>
            issue.closedAtDate &&
            issue.closedAtDate >= dayStart &&
            issue.closedAtDate <= dayEnd &&
            issue.state === "closed",
        )

        const dayMetrics = calculateMetrics(dayCommits, dayPRs, dayIssues)
        const dayScore = calculatePerformanceScore(dayMetrics, 1) // 1 day

        dailyValues.push({
          date: dayStart,
          dateFormatted: dayStart.toLocaleDateString(),
          value: dayScore.overall,
          label: dayStart.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          commits: dayCommits.length,
          prs: dayPRs.length,
          issues: dayIssues.length,
          linesChanged: dayMetrics.linesAdded + dayMetrics.linesRemoved,
          breakdown: dayScore.breakdown,
        })
      }

      // Calculate trend (first half vs second half)
      const halfwayPoint = Math.floor(dailyValues.length / 2)
      const firstHalfAvg =
        dailyValues.slice(0, halfwayPoint).reduce((sum, d) => sum + d.value, 0) / halfwayPoint || 0
      const secondHalfAvg =
        dailyValues.slice(halfwayPoint).reduce((sum, d) => sum + d.value, 0) /
          (dailyValues.length - halfwayPoint) || 0
      const trend = secondHalfAvg - firstHalfAvg

      return {
        score,
        dailyValues,
        chartData: dailyValues.map((d) => ({ label: d.label, value: d.value })),
        trend,
      }
    }, [
      dataStore.allCommits,
      dataStore.allPullRequests,
      dataStore.allIssues,
      selectedAuthorHandle,
      dateRange,
    ])

    // Create the dashboard store with computed data filtered by author
    const dashboardStore = useMemo(() => {
      // Filter commits and PRs by selected author
      const commits = dataStore.allCommits.filter((c) => c.authorHandle === selectedAuthorHandle)
      const prs = dataStore.allPullRequests.filter(
        (pr) => pr.creatorHandle === selectedAuthorHandle,
      )

      logger.debug("Filtered data for author", {
        component: "IndividualDashboard",
        author: selectedAuthorHandle,
        totalCommits: dataStore.allCommits.length,
        filteredCommits: commits.length,
        totalPRs: dataStore.allPullRequests.length,
        filteredPRs: prs.length,
      })

      // Use computed date ranges (up to yesterday)
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      yesterday.setHours(23, 59, 59, 999)
      const currentPeriodStart = dateRange
      const previousPeriodStart = comparisonDateRange

      // Helper function to generate daily chart data
      // Excludes weekends (Saturday/Sunday) with no activity
      const generateDailyChartData = (
        items: Array<{ timestampDate?: Date; createdAtDate?: Date }>,
        startDate: Date,
        endDate: Date,
      ) => {
        const dailyCounts = new Map<string, number>()
        const start = new Date(startDate)
        start.setHours(0, 0, 0, 0)
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)

        // Initialize all days with 0
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split("T")[0]
          dailyCounts.set(dateKey, 0)
        }

        // Count items per day
        items.forEach((item) => {
          const date = item.timestampDate || item.createdAtDate
          if (date && date >= start && date <= end) {
            const dateKey = date.toISOString().split("T")[0]
            dailyCounts.set(dateKey, (dailyCounts.get(dateKey) || 0) + 1)
          }
        })

        // Filter out weekends with no activity
        return Array.from(dailyCounts.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .filter(([dateKey, value]) => {
            const date = new Date(dateKey)
            const dayOfWeek = date.getDay()
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
            // Include if it's not a weekend, or if it's a weekend with activity
            return !isWeekend || value > 0
          })
          .map(([, value]) => ({ value }))
      }

      // Current period metrics (based on selected time range)
      const currentWeekPRs = prs.filter(
        (pr) => pr.createdAtDate >= currentPeriodStart && pr.state === "merged",
      )
      const currentWeekFeatures = commits.filter(
        (c) => c.timestampDate >= currentPeriodStart && c.commitType === "feature",
      )
      const currentWeekBugfixes = commits.filter(
        (c) => c.timestampDate >= currentPeriodStart && c.commitType === "bugfix",
      )
      const currentWeekMaintenance = commits.filter(
        (c) =>
          c.timestampDate >= currentPeriodStart &&
          c.commitType &&
          c.commitType !== "feature" &&
          c.commitType !== "bugfix",
      )

      // Previous period metrics (for comparison)
      const previousWeekPRs = prs.filter(
        (pr) =>
          pr.createdAtDate >= previousPeriodStart &&
          pr.createdAtDate < currentPeriodStart &&
          pr.state === "merged",
      )
      const previousWeekFeatures = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType === "feature",
      )
      const previousWeekBugfixes = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType === "bugfix",
      )
      const previousWeekMaintenance = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType &&
          c.commitType !== "feature" &&
          c.commitType !== "bugfix",
      )

      logger.debug("Calculated individual metrics", {
        component: "IndividualDashboard",
        author: selectedAuthorHandle,
        currentWeekPRs: currentWeekPRs.length,
        currentWeekFeatures: currentWeekFeatures.length,
        currentWeekBugfixes: currentWeekBugfixes.length,
        currentWeekMaintenance: currentWeekMaintenance.length,
      })

      // Create insights with their associated table data
      const insights = [
        {
          id: "pr-throughput",
          insight: {
            id: "pr-throughput",
            subtitle: i18nStore.t("insightMergedPRs"),
            date: timeRangeLabel,
            title: i18nStore.t("insightPRThroughput"),
            value: currentWeekPRs.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekPRs.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekPRs, currentPeriodStart, yesterday),
          },
          tableData: currentWeekPRs.map((pr) => ({
            title: pr.title,
            url: `https://github.com/${pr.repoName}/pull/${pr.number}`,
            repo: pr.repoName,
            date: pr.createdAtDate.toLocaleDateString(),
            additions: pr.additions?.toString() || "0",
            deletions: pr.deletions?.toString() || "0",
            files: pr.changedFiles?.toString() || "0",
          })),
          tableColumns: [
            {
              accessorKey: "title",
              header: "Title",
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("title")}
                </a>
              ),
            },
            { accessorKey: "repo", header: "Repository" },
            { accessorKey: "date", header: "Created" },
            { accessorKey: "additions", header: "+" },
            { accessorKey: "deletions", header: "-" },
            { accessorKey: "files", header: "Files" },
          ],
        },
        {
          id: "features",
          insight: {
            id: "features",
            subtitle: i18nStore.t("insightFeatureCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightFeatures"),
            value: currentWeekFeatures.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekFeatures.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekFeatures, currentPeriodStart, yesterday),
          },
          tableData: currentWeekFeatures.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            repo: commit.repoName,
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: "Message",
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
              ),
            },
            { accessorKey: "repo", header: "Repository" },
            { accessorKey: "date", header: "Date" },
            { accessorKey: "score", header: "Score" },
            { accessorKey: "files", header: "Files" },
          ],
        },
        {
          id: "bugfixes",
          insight: {
            id: "bugfixes",
            subtitle: i18nStore.t("insightBugfixCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightBugfixes"),
            value: currentWeekBugfixes.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekBugfixes.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekBugfixes, currentPeriodStart, yesterday),
          },
          tableData: currentWeekBugfixes.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            repo: commit.repoName,
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: "Message",
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
              ),
            },
            { accessorKey: "repo", header: "Repository" },
            { accessorKey: "date", header: "Date" },
            { accessorKey: "score", header: "Score" },
            { accessorKey: "files", header: "Files" },
          ],
        },
        {
          id: "performance",
          insight: {
            id: "performance",
            subtitle: i18nStore.t("performanceScoreTitle"),
            date: timeRangeLabel,
            title: i18nStore.t("tabPerformance"),
            value: performanceScoreData.score.overall.toFixed(1),
            secondaryText: i18nStore.tf(comparisonKey, {
              count:
                (performanceScoreData.trend >= 0 ? "+" : "") +
                performanceScoreData.trend.toFixed(1),
            }),
            chartData: performanceScoreData.chartData,
          },
          tableData: performanceScoreData.dailyValues
            .map((dayData) => {
              const b = dayData.breakdown

              // Build concise reasoning based on actual activity and its impact
              const reasons = []
              if (dayData.commits > 0) {
                reasons.push(`${dayData.commits} commits`)
              }
              if (dayData.prs > 0) {
                reasons.push(`${dayData.prs} PRs`)
              }
              if (dayData.linesChanged > 0) {
                reasons.push(`${dayData.linesChanged} lines`)
              }
              if (dayData.issues > 0) {
                reasons.push(`${dayData.issues} issues`)
              }

              // Add component scores to show impact on overall score
              const impactScores = []
              if (b.quality > 0) impactScores.push(`Q:${b.quality.toFixed(0)}`)
              if (b.throughput > 0) impactScores.push(`T:${b.throughput.toFixed(0)}`)
              if (b.impact > 0) impactScores.push(`I:${b.impact.toFixed(0)}`)
              if (b.problemSolving > 0) impactScores.push(`PS:${b.problemSolving.toFixed(0)}`)
              if (b.versatility > 0) impactScores.push(`V:${b.versatility.toFixed(0)}`)

              const activityText = reasons.length > 0 ? reasons.join(", ") : "No activity"
              const impactText = impactScores.length > 0 ? ` → ${impactScores.join(", ")}` : ""

              // Add formula explanation showing how the score is calculated
              // Format: Q×30% + T×25% + I×20% + PS×15% + V×10%
              const formulaParts = []
              if (b.quality > 0) formulaParts.push(`${b.quality.toFixed(0)}×30%`)
              if (b.throughput > 0) formulaParts.push(`${b.throughput.toFixed(0)}×25%`)
              if (b.impact > 0) formulaParts.push(`${b.impact.toFixed(0)}×20%`)
              if (b.problemSolving > 0) formulaParts.push(`${b.problemSolving.toFixed(0)}×15%`)
              if (b.versatility > 0) formulaParts.push(`${b.versatility.toFixed(0)}×10%`)

              const formulaText = formulaParts.length > 0 ? ` = ${formulaParts.join(" + ")}` : ""

              // Get weekday name
              const weekdayNames = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ]
              const weekday = weekdayNames[dayData.date.getDay()]

              // Check if it's a weekend with no activity
              const isWeekend = dayData.date.getDay() === 0 || dayData.date.getDay() === 6
              const hasActivity = dayData.commits > 0 || dayData.prs > 0 || dayData.issues > 0
              const isExcluded = isWeekend && !hasActivity

              return {
                date: dayData.dateFormatted,
                weekday: weekday,
                value: dayData.value.toFixed(1),
                reasoning: activityText + impactText + formulaText,
                isExcluded: isExcluded,
              }
            })
            .reverse(), // Most recent first
          tableColumns: [
            {
              accessorKey: "date",
              header: i18nStore.t("tableColumnDate"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; date: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.date}
                </span>
              ),
            },
            {
              accessorKey: "weekday",
              header: i18nStore.t("tableColumnWeekday"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; weekday: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.weekday}
                </span>
              ),
            },
            {
              accessorKey: "value",
              header: i18nStore.t("tableColumnValue"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; value: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.value}
                </span>
              ),
            },
            {
              accessorKey: "reasoning",
              header: i18nStore.t("tableColumnReasoning"),
              cell: ({
                row,
              }: {
                row: { original: { isExcluded: boolean; reasoning: string } }
              }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.reasoning}
                </span>
              ),
            },
          ],
        },
        // Maintenance insight kept for potential future use
        {
          id: "maintenance",
          insight: {
            id: "maintenance",
            subtitle: i18nStore.t("insightMaintenanceCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightMaintenance"),
            value: currentWeekMaintenance.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekMaintenance.length.toString(),
            }),
            chartData: generateDailyChartData(
              currentWeekMaintenance,
              currentPeriodStart,
              yesterday,
            ),
          },
          tableData: currentWeekMaintenance.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            repo: commit.repoName,
            type: commit.commitType || "other",
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: "Message",
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
              ),
            },
            { accessorKey: "repo", header: "Repository" },
            { accessorKey: "type", header: "Type" },
            { accessorKey: "date", header: "Date" },
            { accessorKey: "score", header: "Score" },
            { accessorKey: "files", header: "Files" },
          ],
        },
      ]

      return InsightDashboardStore.create({
        insights: insights.slice(0, 4), // Only show pr-throughput, features, bugfixes, and performance
        selectedInsightId: "pr-throughput",
      })
    }, [
      dataStore.allCommits,
      dataStore.allPullRequests,
      dataStore.allIssues,
      selectedAuthorHandle,
      i18nStore.t,
      i18nStore.tf,
      dateRange,
      comparisonDateRange,
      timeRangeLabel,
      comparisonKey,
      performanceScoreData,
    ])

    // Compute bar chart data for individual commits
    const barChartData = useMemo(() => {
      const selectedClassifications = settingsStore.selectedClassificationsArray

      // Map commit types to avoid reserved words (for display purposes only)
      const mapCommitType = (type: string | undefined): string => {
        if (type === "style") return "styling"
        return type || "unclassified"
      }

      // Filter commits by selected author and selected classifications
      // Note: Use original commitType for filtering, mapCommitType is only for display
      const commits = dataStore.allCommits.filter((c) => {
        const classType = c.commitType || "unclassified"
        return (
          c.authorHandle === selectedAuthorHandle &&
          selectedClassifications.includes(classType as any)
        )
      })

      // Define the order of complexity classes and commit types
      const complexityOrder: ComplexityClass[] = ["XS", "S", "M", "L", "XL"]
      const commitTypeOrder = [
        "feature",
        "bugfix",
        "refactoring",
        "ci",
        "tests",
        "docs",
        "chore",
        "perf",
        "revert",
        "merge",
        "archive",
        "styling", // Renamed from "style" to avoid React reserved prop conflict
        "other",
      ]

      // Chart 1: Classification (x-axis) stacked by Size
      const byClassification = new Map<string, Record<ComplexityClass, number>>()

      for (const commitType of commitTypeOrder) {
        byClassification.set(commitType, {
          XS: 0,
          S: 0,
          M: 0,
          L: 0,
          XL: 0,
        })
      }

      byClassification.set("unclassified", {
        XS: 0,
        S: 0,
        M: 0,
        L: 0,
        XL: 0,
      })

      for (const commit of commits) {
        const type = mapCommitType(commit.commitType)
        const size = commit.complexityClass
        const counts = byClassification.get(type)
        if (counts && size in counts) {
          counts[size] = (counts[size] || 0) + 1
        }
      }

      const classificationData = Array.from(byClassification.entries())
        .filter(([type, counts]) => {
          // Map "styling" back to "style" for the selectedClassifications check
          const originalType = type === "styling" ? "style" : type
          return (
            selectedClassifications.includes(originalType as any) &&
            Object.values(counts).some((v) => v > 0)
          )
        })
        .map(([type, counts]) => ({
          classification: type,
          ...counts,
        }))

      // Chart 2: Size (x-axis) stacked by Classification
      const bySize = new Map<ComplexityClass, Record<string, number>>()

      for (const size of complexityOrder) {
        const typeCounts: Record<string, number> = {}
        for (const type of [...commitTypeOrder, "unclassified"]) {
          typeCounts[type] = 0
        }
        bySize.set(size, typeCounts)
      }

      for (const commit of commits) {
        const type = mapCommitType(commit.commitType)
        const size = commit.complexityClass
        const counts = bySize.get(size)
        if (counts) {
          counts[type] = (counts[type] || 0) + 1
        }
      }

      const activeTypes = new Set<string>()
      for (const [, counts] of bySize.entries()) {
        for (const [type, count] of Object.entries(counts)) {
          // Map "styling" back to "style" for the selectedClassifications check
          const originalType = type === "styling" ? "style" : type
          if (count > 0 && selectedClassifications.includes(originalType as any)) {
            activeTypes.add(type)
          }
        }
      }

      const sizeData = complexityOrder.map((size) => {
        const counts = bySize.get(size)
        if (!counts) return { size }
        const entry: Record<string, string | number> = { size }
        for (const type of activeTypes) {
          entry[type] = counts[type] || 0
        }
        return entry
      })

      return {
        classificationData,
        sizeData,
        complexityOrder,
        activeTypes: Array.from(activeTypes),
      }
    }, [dataStore.allCommits, selectedAuthorHandle, settingsStore.selectedClassificationsArray])

    // Calculate PR metrics for the new PR tab
    const prMetrics = useMemo(() => {
      const prs = dataStore.allPullRequests.filter(
        (pr) => pr.creatorHandle === selectedAuthorHandle,
      )
      const now = new Date()
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

      // Current month PRs
      const currentMonthPRs = prs.filter(
        (pr) => pr.mergedAtDate && pr.mergedAtDate >= oneMonthAgo && pr.state === "merged",
      )

      // Previous month PRs (for comparison)
      const previousMonthPRs = prs.filter(
        (pr) =>
          pr.mergedAtDate &&
          pr.mergedAtDate >= twoMonthsAgo &&
          pr.mergedAtDate < oneMonthAgo &&
          pr.state === "merged",
      )

      // Calculate percentage change
      const currentCount = currentMonthPRs.length
      const previousCount = previousMonthPRs.length
      const percentageChange =
        previousCount > 0 ? ((currentCount - previousCount) / previousCount) * 100 : 0

      // Generate daily PR count for chart (last 30 days)
      const dailyPRs = new Map<string, number>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyPRs.set(dateKey, 0)
      }

      currentMonthPRs.forEach((pr) => {
        if (pr.mergedAtDate) {
          const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
          dailyPRs.set(dateKey, (dailyPRs.get(dateKey) || 0) + 1)
        }
      })

      const prChartData = Array.from(dailyPRs.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      // Calculate code changes (additions + deletions) for current month
      const dailyCodeChanges = new Map<string, { additions: number; deletions: number }>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyCodeChanges.set(dateKey, { additions: 0, deletions: 0 })
      }

      currentMonthPRs.forEach((pr) => {
        if (pr.mergedAtDate) {
          const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
          const current = dailyCodeChanges.get(dateKey) || {
            additions: 0,
            deletions: 0,
          }
          dailyCodeChanges.set(dateKey, {
            additions: current.additions + pr.additions,
            deletions: current.deletions + pr.deletions,
          })
        }
      })

      const codeChangesChartData = Array.from(dailyCodeChanges.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, { additions, deletions }]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: additions + deletions,
        }))

      return {
        currentCount,
        previousCount,
        percentageChange,
        prChartData,
        codeChangesChartData,
        trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
          | "up"
          | "down"
          | "neutral",
      }
    }, [dataStore.allPullRequests, selectedAuthorHandle])

    // Calculate Issue metrics for the new Issues tab
    const issueMetrics = useMemo(() => {
      const issues = dataStore.allIssues.filter(
        (issue) => issue.creatorHandle === selectedAuthorHandle,
      )
      const now = new Date()
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

      // Current month issues created
      const currentMonthCreated = issues.filter((issue) => issue.createdAtDate >= oneMonthAgo)

      // Previous month issues created (for comparison)
      const previousMonthCreated = issues.filter(
        (issue) => issue.createdAtDate >= twoMonthsAgo && issue.createdAtDate < oneMonthAgo,
      )

      // Current month issues closed
      const currentMonthClosed = issues.filter(
        (issue) =>
          issue.closedAtDate && issue.closedAtDate >= oneMonthAgo && issue.state === "closed",
      )

      // Previous month issues closed (for comparison)
      const previousMonthClosed = issues.filter(
        (issue) =>
          issue.closedAtDate &&
          issue.closedAtDate >= twoMonthsAgo &&
          issue.closedAtDate < oneMonthAgo &&
          issue.state === "closed",
      )

      // Calculate percentage changes
      const createdCount = currentMonthCreated.length
      const previousCreatedCount = previousMonthCreated.length
      const createdPercentageChange =
        previousCreatedCount > 0
          ? ((createdCount - previousCreatedCount) / previousCreatedCount) * 100
          : 0

      const closedCount = currentMonthClosed.length
      const previousClosedCount = previousMonthClosed.length
      const closedPercentageChange =
        previousClosedCount > 0
          ? ((closedCount - previousClosedCount) / previousClosedCount) * 100
          : 0

      // Generate daily issue created count for chart (last 30 days)
      const dailyCreated = new Map<string, number>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyCreated.set(dateKey, 0)
      }

      currentMonthCreated.forEach((issue) => {
        const dateKey = issue.createdAtDate.toISOString().split("T")[0]
        dailyCreated.set(dateKey, (dailyCreated.get(dateKey) || 0) + 1)
      })

      const createdChartData = Array.from(dailyCreated.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      // Generate daily issue closed count for chart (last 30 days)
      const dailyClosed = new Map<string, number>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyClosed.set(dateKey, 0)
      }

      currentMonthClosed.forEach((issue) => {
        if (issue.closedAtDate) {
          const dateKey = issue.closedAtDate.toISOString().split("T")[0]
          dailyClosed.set(dateKey, (dailyClosed.get(dateKey) || 0) + 1)
        }
      })

      const closedChartData = Array.from(dailyClosed.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      return {
        createdCount,
        previousCreatedCount,
        createdPercentageChange,
        createdChartData,
        createdTrend: (createdPercentageChange > 0
          ? "up"
          : createdPercentageChange < 0
            ? "down"
            : "neutral") as "up" | "down" | "neutral",
        closedCount,
        previousClosedCount,
        closedPercentageChange,
        closedChartData,
        closedTrend: (closedPercentageChange > 0
          ? "up"
          : closedPercentageChange < 0
            ? "down"
            : "neutral") as "up" | "down" | "neutral",
      }
    }, [dataStore.allIssues, selectedAuthorHandle])

    // Calculate releases metrics for individual
    const releasesMetrics = useMemo(() => {
      const now = new Date()

      // Helper to get Monday of the current week
      const getMondayOfWeek = (date: Date): Date => {
        const d = new Date(date)
        const day = d.getDay()
        const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
        return new Date(d.setDate(diff))
      }

      // Helper to get Sunday of a given Monday
      const getSundayOfWeek = (monday: Date): Date => {
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        return sunday
      }

      // Get Monday of current week
      const currentWeekMonday = getMondayOfWeek(now)
      currentWeekMonday.setHours(0, 0, 0, 0)

      // Calculate start of 12 weeks ago (Monday of that week)
      const twelveWeeksAgoMonday = new Date(currentWeekMonday)
      twelveWeeksAgoMonday.setDate(currentWeekMonday.getDate() - 12 * 7)

      // Calculate start of 6 weeks ago (Monday of that week)
      const sixWeeksAgoMonday = new Date(currentWeekMonday)
      sixWeeksAgoMonday.setDate(currentWeekMonday.getDate() - 6 * 7)

      // Filter releases by author (excluding drafts and prereleases)
      const recentReleases = dataStore.allReleases.filter(
        (release) =>
          release.createdAtDate >= twelveWeeksAgoMonday &&
          !release.draft &&
          !release.prerelease &&
          release.authorHandle === selectedAuthorHandle,
      )

      // Count total releases in last 12 weeks
      const totalReleases = recentReleases.length

      // Create weekly buckets for trend (last 12 weeks)
      const weeklyTrend: Array<{ label: string; value: number }> = []
      for (let i = 0; i < 12; i++) {
        const weekMonday = new Date(currentWeekMonday)
        weekMonday.setDate(currentWeekMonday.getDate() - (11 - i) * 7)
        const weekSunday = getSundayOfWeek(weekMonday)
        weekSunday.setHours(23, 59, 59, 999)

        const count = recentReleases.filter(
          (release) => release.createdAtDate >= weekMonday && release.createdAtDate <= weekSunday,
        ).length

        weeklyTrend.push({
          label: weekMonday.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: count,
        })
      }

      // Calculate percentage change (comparing first 6 weeks vs last 6 weeks)
      const firstHalf = weeklyTrend.slice(0, 6).reduce((sum, week) => sum + week.value, 0)
      const secondHalf = weeklyTrend.slice(6, 12).reduce((sum, week) => sum + week.value, 0)
      const percentageChange = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0

      // Create stacked bar chart data for last 6 weeks by repository
      const last6WeeksReleases = dataStore.allReleases.filter(
        (release) =>
          release.createdAtDate >= sixWeeksAgoMonday &&
          !release.draft &&
          !release.prerelease &&
          release.authorHandle === selectedAuthorHandle,
      )

      // Get all unique repositories
      const repositories = Array.from(new Set(last6WeeksReleases.map((r) => r.repoName))).sort()

      // Create weekly buckets for stacked chart
      const stackedData: Array<Record<string, string | number>> = []
      for (let i = 0; i < 6; i++) {
        const weekMonday = new Date(currentWeekMonday)
        weekMonday.setDate(currentWeekMonday.getDate() - (5 - i) * 7)
        const weekSunday = getSundayOfWeek(weekMonday)
        weekSunday.setHours(23, 59, 59, 999)

        const weekData: Record<string, string | number> = {
          week: weekMonday.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
        }

        // Count releases per repository for this week
        repositories.forEach((repo) => {
          const count = last6WeeksReleases.filter(
            (release) =>
              release.repoName === repo &&
              release.createdAtDate >= weekMonday &&
              release.createdAtDate <= weekSunday,
          ).length
          weekData[repo] = count
        })

        stackedData.push(weekData)
      }

      return {
        totalReleases,
        weeklyTrend,
        percentageChange,
        trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
          | "up"
          | "down"
          | "neutral",
        stackedData,
        repositories,
      }
    }, [dataStore.allReleases, selectedAuthorHandle])

    // Calculate repository commits and PRs data for the grouped bar chart
    const repositoryCommitsAndPRsData = useMemo(() => {
      const commits = dataStore.allCommits.filter(
        (c) => c.authorHandle === selectedAuthorHandle && c.timestampDate >= dateRange,
      )
      const prs = dataStore.allPullRequests.filter(
        (pr) => pr.creatorHandle === selectedAuthorHandle && pr.createdAtDate >= dateRange,
      )

      // Group commits by repository
      const repoCommitCounts = new Map<string, number>()
      commits.forEach((commit) => {
        repoCommitCounts.set(commit.repoName, (repoCommitCounts.get(commit.repoName) || 0) + 1)
      })

      // Group PRs by repository
      const repoPRCounts = new Map<string, number>()
      prs.forEach((pr) => {
        repoPRCounts.set(pr.repoName, (repoPRCounts.get(pr.repoName) || 0) + 1)
      })

      // Get all unique repositories
      const allRepos = new Set([...repoCommitCounts.keys(), ...repoPRCounts.keys()])

      // Convert to array format for grouped bar chart, sorted by total activity descending
      return Array.from(allRepos)
        .map((repo) => ({
          label: repo,
          group1: repoCommitCounts.get(repo) || 0,
          group2: repoPRCounts.get(repo) || 0,
        }))
        .sort((a, b) => b.group1 + b.group2 - (a.group1 + a.group2))
    }, [dataStore.allCommits, dataStore.allPullRequests, selectedAuthorHandle, dateRange])

    // Calculate commits table data for Score tab
    const commitsTableData = useMemo(() => {
      const commits = dataStore.allCommits.filter(
        (c) => c.authorHandle === selectedAuthorHandle && c.timestampDate >= dateRange,
      )

      return commits.map((commit) => ({
        id: commit.hash,
        title: commit.message.slice(0, 100) + (commit.message.length > 100 ? "..." : ""),
        fullMessage: commit.message,
        url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
        repo: commit.repoName,
        created: commit.timestampDate,
        createdFormatted: commit.timestampDate.toLocaleDateString(),
        score: commit.score,
        classification: commit.commitType || "unclassified",
        complexityClass: commit.complexityClass,
      }))
    }, [dataStore.allCommits, selectedAuthorHandle, dateRange])

    // Calculate PRs table data for PRs tab
    const prsTableData = useMemo(() => {
      const prs = dataStore.allPullRequests.filter(
        (pr) => pr.creatorHandle === selectedAuthorHandle && pr.createdAtDate >= dateRange,
      )

      return prs.map((pr) => ({
        id: pr.id,
        title: pr.title,
        url: `https://github.com/${pr.repoName}/pull/${pr.number}`,
        repo: pr.repoName,
        createdAtDate: pr.createdAtDate,
        createdFormatted: pr.createdAtDate.toLocaleDateString(),
      }))
    }, [dataStore.allPullRequests, selectedAuthorHandle, dateRange])

    // Calculate Issues table data for Issues tab
    const issuesTableData = useMemo(() => {
      const issues = dataStore.allIssues.filter(
        (issue) => issue.creatorHandle === selectedAuthorHandle && issue.createdAtDate >= dateRange,
      )

      return issues.map((issue) => ({
        id: issue.id,
        title: issue.title,
        url: `https://github.com/${issue.repoName}/issues/${issue.number}`,
        repo: issue.repoName,
        createdAtDate: issue.createdAtDate,
        createdFormatted: issue.createdAtDate.toLocaleDateString(),
      }))
    }, [dataStore.allIssues, selectedAuthorHandle, dateRange])

    // Create tabs
    const tabs: TabDefinition[] = [
      {
        id: "insights",
        label: i18nStore.t("tabPerformance"),
        content: (
          <div className="space-y-6">
            {/* Always show quick insight cards */}
            <InsightDashboard
              store={dashboardStore}
              tableTitle={i18nStore.t("chartDetails")}
              dataTableTranslations={{
                filtersButton: i18nStore.t("dataTableFilters"),
                filterBy: i18nStore.t("dataTableFilterBy"),
                noResults: i18nStore.t("dataTableNoResults"),
                rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                page: i18nStore.t("dataTablePage"),
                of: i18nStore.t("dataTableOf"),
                totalRows: i18nStore.t("dataTableTotalRows"),
              }}
              searchPlaceholder={i18nStore.t("dataTableSearch")}
              hideTable={true}
            />

            {/* Show performance explanation and details table when performance is selected */}
            {dashboardStore.selectedInsight?.id === "performance" &&
              dashboardStore.selectedTableData.length > 0 &&
              dashboardStore.selectedTableColumns.length > 0 && (
                <div className="space-y-4">
                  {/* Performance Explanation */}
                  <div className="rounded-lg border bg-card p-6">
                    <h3 className="text-lg font-semibold mb-3">
                      {i18nStore.t("performanceMethodologyTitle")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {i18nStore.t("performanceMethodologyDescription")}
                    </p>
                    <ul className="space-y-2 text-sm mb-4">
                      <li>
                        <strong>{i18nStore.t("performanceQualityWeight")}</strong>:{" "}
                        {i18nStore.t("performanceQualityDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceThroughputWeight")}</strong>:{" "}
                        {i18nStore.t("performanceThroughputDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceImpactWeight")}</strong>:{" "}
                        {i18nStore.t("performanceImpactDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceProblemSolvingWeight")}</strong>:{" "}
                        {i18nStore.t("performanceProblemSolvingDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceVersatilityWeight")}</strong>:{" "}
                        {i18nStore.t("performanceVersatilityDesc")}
                      </li>
                    </ul>
                    <p className="text-sm text-muted-foreground italic border-t pt-3">
                      {i18nStore.t("performanceScoreNote")}
                    </p>
                  </div>

                  {/* Performance Daily Values Table */}
                  <DataTable
                    title={i18nStore.t("performanceDailyValuesTitle")}
                    columns={dashboardStore.selectedTableColumns as any}
                    data={dashboardStore.selectedTableData}
                    enableSearch={true}
                    searchPlaceholder={i18nStore.t("dataTableSearch")}
                    enableCsvExport={true}
                    csvFilename={`performance-daily-${selectedAuthorHandle}.csv`}
                    initialSorting={[{ id: "date", desc: true }]}
                    translations={{
                      filtersButton: i18nStore.t("dataTableFilters"),
                      filterBy: i18nStore.t("dataTableFilterBy"),
                      noResults: i18nStore.t("dataTableNoResults"),
                      rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                      page: i18nStore.t("dataTablePage"),
                      of: i18nStore.t("dataTableOf"),
                      totalRows: i18nStore.t("dataTableTotalRows"),
                    }}
                  />
                </div>
              )}

            {/* Repository Commits and PRs Grouped Bar Chart - only show when NOT performance */}
            {dashboardStore.selectedInsight?.id !== "performance" &&
              repositoryCommitsAndPRsData.length > 0 && (
                <GroupedBarChart
                  title={i18nStore.t("chartRepositoryPRsAndCommits")}
                  description={i18nStore.tf("chartRepositoryPRsAndCommitsDesc", {
                    timeRange: timeRangeLabel.toLowerCase(),
                  })}
                  data={repositoryCommitsAndPRsData}
                  group1Label={i18nStore.t("commits")}
                  group2Label={i18nStore.t("pullRequests")}
                />
              )}

            {/* Details Table - only show when NOT performance */}
            {dashboardStore.selectedInsight?.id !== "performance" &&
              dashboardStore.selectedTableData.length > 0 &&
              dashboardStore.selectedTableColumns.length > 0 && (
                <DataTable
                  title={i18nStore.t("chartDetails")}
                  columns={dashboardStore.selectedTableColumns as any}
                  data={dashboardStore.selectedTableData}
                  enableSearch={true}
                  searchPlaceholder={i18nStore.t("dataTableSearch")}
                  enableCsvExport={true}
                  csvFilename="insight-data.csv"
                  translations={{
                    filtersButton: i18nStore.t("dataTableFilters"),
                    filterBy: i18nStore.t("dataTableFilterBy"),
                    noResults: i18nStore.t("dataTableNoResults"),
                    rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                    page: i18nStore.t("dataTablePage"),
                    of: i18nStore.t("dataTableOf"),
                    totalRows: i18nStore.t("dataTableTotalRows"),
                  }}
                />
              )}
          </div>
        ),
      },
      {
        id: "commits",
        label: i18nStore.t("tabScore"),
        content: (
          <div className="space-y-6">
            {/* Stacked Bar Charts */}
            <div className="grid gap-6 md:grid-cols-2">
              <StackedBarChart
                title={i18nStore.t("chartCommitsByClassification")}
                description="Number of commits grouped by classification (commit type) and stacked by complexity size"
                data={barChartData.classificationData}
                categoryKey="classification"
                stackKeys={barChartData.complexityOrder}
              />
              <StackedBarChart
                title={i18nStore.t("chartCommitsBySize")}
                description="Number of commits grouped by complexity size and stacked by classification (commit type)"
                data={barChartData.sizeData}
                categoryKey="size"
                stackKeys={barChartData.activeTypes}
              />
            </div>

            {/* Commits Table */}
            <DataTable
              title={i18nStore.t("commitsTableTitle")}
              columns={[
                {
                  accessorKey: "title",
                  header: i18nStore.t("tableColumnTitle"),
                  cell: ({
                    row,
                  }: {
                    row: {
                      original: { url: string; fullMessage: string }
                      getValue: (key: string) => string
                    }
                  }) => (
                    <a
                      href={row.original.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      title={row.original.fullMessage}
                    >
                      {row.getValue("title")}
                    </a>
                  ),
                },
                {
                  accessorKey: "repo",
                  header: i18nStore.t("tableColumnRepo"),
                },
                {
                  accessorKey: "createdFormatted",
                  header: i18nStore.t("tableColumnCreated"),
                  id: "created",
                },
                {
                  accessorKey: "score",
                  header: i18nStore.t("tableColumnScore"),
                  cell: ({ row }: { row: { getValue: (key: string) => number } }) => {
                    const score = row.getValue("score")
                    return (
                      <Badge
                        style={{
                          backgroundColor: getScoreBadgeColor(score),
                          color: "white",
                        }}
                      >
                        {score}
                      </Badge>
                    )
                  },
                },
                {
                  accessorKey: "classification",
                  header: i18nStore.t("tableColumnClassification"),
                  cell: ({ row }: { row: { getValue: (key: string) => string } }) => {
                    const classification = row.getValue("classification")
                    const translationKey =
                      `classification${classification.charAt(0).toUpperCase() + classification.slice(1)}` as keyof typeof import("../i18n/en").en
                    return (
                      <Badge
                        style={{
                          backgroundColor: getClassificationBadgeColor(classification),
                          color: "white",
                        }}
                      >
                        {i18nStore.t(translationKey) || classification}
                      </Badge>
                    )
                  },
                },
              ]}
              data={commitsTableData}
              enableSearch={true}
              searchPlaceholder={i18nStore.t("searchCommits")}
              enableCsvExport={true}
              csvFilename={`commits-${selectedAuthorHandle}.csv`}
              initialSorting={[{ id: "created", desc: true }]}
              translations={{
                filtersButton: i18nStore.t("dataTableFilters"),
                filterBy: i18nStore.t("dataTableFilterBy"),
                noResults: i18nStore.t("dataTableNoResults"),
                rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                page: i18nStore.t("dataTablePage"),
                of: i18nStore.t("dataTableOf"),
                totalRows: i18nStore.t("dataTableTotalRows"),
              }}
            />
          </div>
        ),
      },
      {
        id: "prs",
        label: i18nStore.t("tabPRs"),
        content: (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <MetricsCard
                title={i18nStore.t("chartPRsMerged")}
                description={i18nStore.tf("chartPRsMergedDesc", {
                  count: prMetrics.currentCount.toString(),
                  prevCount: prMetrics.previousCount.toString(),
                })}
                percentage={Math.abs(prMetrics.percentageChange)}
                trend={prMetrics.trend}
                chartData={prMetrics.prChartData}
                chartType="bar"
              />
              <MetricsCard
                title={i18nStore.t("chartCodeChanges")}
                description={i18nStore.t("chartCodeChangesDesc")}
                percentage={0}
                trend="neutral"
                chartData={prMetrics.codeChangesChartData}
                chartType="line"
              />
            </div>

            {/* PRs Table */}
            <DataTable
              title={i18nStore.t("tabPRs")}
              columns={[
                {
                  accessorKey: "title",
                  header: i18nStore.t("tableColumnTitle"),
                  cell: ({
                    row,
                  }: {
                    row: {
                      original: { url: string }
                      getValue: (key: string) => string
                    }
                  }) => (
                    <a
                      href={row.original.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {row.getValue("title")}
                    </a>
                  ),
                },
                {
                  accessorKey: "repo",
                  header: i18nStore.t("tableColumnRepo"),
                },
              ]}
              data={prsTableData}
              enableSearch={true}
              searchPlaceholder={i18nStore.t("dataTableSearch")}
              enableCsvExport={true}
              csvFilename={`prs-${selectedAuthorHandle}.csv`}
              initialSorting={[{ id: "createdAtDate", desc: true }]}
              translations={{
                filtersButton: i18nStore.t("dataTableFilters"),
                filterBy: i18nStore.t("dataTableFilterBy"),
                noResults: i18nStore.t("dataTableNoResults"),
                rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                page: i18nStore.t("dataTablePage"),
                of: i18nStore.t("dataTableOf"),
                totalRows: i18nStore.t("dataTableTotalRows"),
              }}
            />
          </div>
        ),
      },
      {
        id: "issues",
        label: i18nStore.t("tabIssues"),
        content: (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <MetricsCard
                title={i18nStore.t("chartIssuesCreated")}
                description={i18nStore.tf("chartIssuesCreatedDesc", {
                  count: issueMetrics.createdCount.toString(),
                  prevCount: issueMetrics.previousCreatedCount.toString(),
                })}
                percentage={Math.abs(issueMetrics.createdPercentageChange)}
                trend={issueMetrics.createdTrend}
                chartData={issueMetrics.createdChartData}
                chartType="bar"
              />
              <MetricsCard
                title={i18nStore.t("chartIssuesClosed")}
                description={i18nStore.tf("chartIssuesClosedDesc", {
                  count: issueMetrics.closedCount.toString(),
                  prevCount: issueMetrics.previousClosedCount.toString(),
                })}
                percentage={Math.abs(issueMetrics.closedPercentageChange)}
                trend={issueMetrics.closedTrend}
                chartData={issueMetrics.closedChartData}
                chartType="bar"
              />
            </div>

            {/* Issues Table */}
            <DataTable
              title={i18nStore.t("tabIssues")}
              columns={[
                {
                  accessorKey: "title",
                  header: i18nStore.t("tableColumnTitle"),
                  cell: ({
                    row,
                  }: {
                    row: {
                      original: { url: string }
                      getValue: (key: string) => string
                    }
                  }) => (
                    <a
                      href={row.original.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {row.getValue("title")}
                    </a>
                  ),
                },
                {
                  accessorKey: "repo",
                  header: i18nStore.t("tableColumnRepo"),
                },
              ]}
              data={issuesTableData}
              enableSearch={true}
              searchPlaceholder={i18nStore.t("dataTableSearch")}
              enableCsvExport={true}
              csvFilename={`issues-${selectedAuthorHandle}.csv`}
              initialSorting={[{ id: "createdAtDate", desc: true }]}
              translations={{
                filtersButton: i18nStore.t("dataTableFilters"),
                filterBy: i18nStore.t("dataTableFilterBy"),
                noResults: i18nStore.t("dataTableNoResults"),
                rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                page: i18nStore.t("dataTablePage"),
                of: i18nStore.t("dataTableOf"),
                totalRows: i18nStore.t("dataTableTotalRows"),
              }}
            />
          </div>
        ),
      },
      {
        id: "releases",
        label: i18nStore.t("tabReleases"),
        content: (
          <div className="grid gap-6">
            {/* Releases Trend Card */}
            <MetricsCard
              title={i18nStore.t("releasesTitle")}
              description={i18nStore.t("releasesDescription")}
              percentage={Math.abs(releasesMetrics.percentageChange)}
              trend={releasesMetrics.trend}
              chartData={releasesMetrics.weeklyTrend}
              chartType="line"
              trendLabel={i18nStore.t("chartTrend")}
            />

            {/* Stacked Bar Chart - Releases by Repository */}
            <StackedBarChart
              title={i18nStore.t("releasesChartTitle")}
              description={i18nStore.t("releasesChartDescription")}
              data={releasesMetrics.stackedData}
              categoryKey="week"
              stackKeys={releasesMetrics.repositories}
            />
          </div>
        ),
      },
    ]

    // Log dashboard render
    logger.debug("Rendering IndividualDashboard", {
      component: "IndividualDashboard",
      author: selectedAuthorHandle,
    })

    // If no authors available, show message
    if (authors.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          {i18nStore.t("dashboardIndividualNoData")}
        </div>
      )
    }

    // Helper to safely encode GitHub handles for URLs
    const encodeGitHubHandle = (handle: string): string => {
      return encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))
    }

    // Helper to get GitHub avatar URL with validation
    const getGitHubAvatarUrl = (handle: string, size = 80): string => {
      const safeHandle = encodeGitHubHandle(handle)
      return `https://github.com/${safeHandle}.png?size=${size}`
    }

    return (
      <div className="space-y-6">
        {/* Author selector with clickable GitHub link */}
        <div className="space-y-4">
          <PageHeader
            title=""
            subtitle=""
            selectOptions={authorOptions}
            selectedOptionId={selectedAuthorHandle}
            onSelectChange={handleAuthorChange}
            selectPlaceholder={i18nStore.t("selectDeveloper")}
          />
          {/* Clickable avatar and name section */}
          <a
            href={`https://github.com/${selectedAuthorHandle}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity w-fit"
          >
            <img
              src={getGitHubAvatarUrl(selectedAuthorHandle)}
              alt={selectedAuthorHandle}
              className="h-12 w-12 rounded-full"
              onError={(e) => {
                e.currentTarget.style.display = "none"
              }}
            />
            <div>
              <p className="text-sm text-muted-foreground">
                {i18nStore.tf("dashboardIndividualSubtitle", {
                  author: selectedAuthorHandle,
                })}
              </p>
            </div>
          </a>
        </div>
        <TabsWithStore
          tabs={tabs}
          selectedTab={selectedTab}
          defaultSelectedTab="insights"
          onTabChange={onTabChange}
        />
      </div>
    )
  },
)
